import { useSyncExternalStore } from "react";
import { translate } from "@/i18n";
import { libNoticesMessages } from "@/i18n/messages/lib-notices";
import { clearSignupAttribution } from "./signup-attribution";

const KEY = "denker.privacy-consent.v1";
export type PrivacyChoices = {
  analytics: boolean;
  diagnostics: boolean;
};
const OFF: PrivacyChoices = {
  analytics: false,
  diagnostics: false,
};
let account: string | null | undefined;
let analyticsConfirmed = false;
let diagnosticsConfirmed = false;
let revision = 0;
let epoch = 0;
let analyticsGeneration = 0;
let lastAnalyticsAllowed = false;
let lastAnalyticsAccount = account;
let error: string | null = null;
let diagnosticsError: string | null = null;
let storageFailed = false;
let accountAnalytics = false;
let accountAnalyticsExcluded = false;
const listeners = new Set<() => void>();
type PrivacySnapshot = PrivacyChoices & {
  /** What Settings shows: the device choice, else the account's confirmed one. */
  analyticsSetting: boolean;
  revision: number;
  error: string | null;
};
let snapshot: PrivacySnapshot = {
  ...OFF,
  analyticsSetting: false,
  revision,
  error,
};

function readRecord(): Record<string, unknown> | null {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) ?? "null");
    return saved?.version === 1 ? saved : null;
  } catch {
    return null;
  }
}

function read(): PrivacyChoices {
  const saved = readRecord();
  return {
    analytics: saved?.analytics === true,
    diagnostics: saved?.diagnostics === true,
  };
}

/**
 * True once analytics was explicitly chosen on this device. Records written
 * before `analyticsChosen` existed count as chosen.
 */
export function hasDeviceAnalyticsChoice(): boolean {
  const saved = readRecord();
  return saved !== null && saved.analyticsChosen !== false;
}

function save(choices: PrivacyChoices, analyticsChosen: boolean): boolean {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ version: 1, ...choices, analyticsChosen }),
    );
    return true;
  } catch {
    return false;
  }
}

function currentSnapshot(): PrivacySnapshot {
  const choices = read();
  return {
    ...choices,
    analyticsSetting:
      choices.analytics || (accountAnalytics && !hasDeviceAnalyticsChoice()),
    revision,
    error: error ?? diagnosticsError,
  };
}

function emit(): void {
  epoch += 1;
  const allowed = analyticsAllowed();
  if (allowed !== lastAnalyticsAllowed || account !== lastAnalyticsAccount)
    analyticsGeneration += 1;
  lastAnalyticsAllowed = allowed;
  lastAnalyticsAccount = account;
  snapshot = currentSnapshot();
  listeners.forEach((listener) => listener());
}

export function subscribePrivacyConsent(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPrivacyConsent() {
  return snapshot;
}
export function privacyEpoch(): number {
  return epoch;
}
export function analyticsEpoch(): number {
  return analyticsGeneration;
}
export function getPrivacyAccount() {
  return account;
}
export function analyticsAllowed(): boolean {
  return (
    !storageFailed &&
    !accountAnalyticsExcluded &&
    read().analytics &&
    account !== undefined &&
    (account === null || analyticsConfirmed)
  );
}
export function diagnosticsAllowed(): boolean {
  return !storageFailed && read().diagnostics && diagnosticsConfirmed;
}
export function usePrivacyConsent() {
  return useSyncExternalStore(
    subscribePrivacyConsent,
    getPrivacyConsent,
    getPrivacyConsent,
  );
}

/** Explicit choices only. Missing/unreadable storage never grants permission. */
export function setPrivacyConsent(next: Partial<PrivacyChoices>): void {
  if (next.analytics === false) clearSignupAttribution();
  const analyticsChosen =
    next.analytics !== undefined || hasDeviceAnalyticsChoice();
  if (next.analytics !== undefined) {
    analyticsConfirmed = false;
    revision += 1;
  }
  if (next.diagnostics !== undefined) {
    diagnosticsConfirmed = false;
    diagnosticsError = null;
  }
  store({ ...read(), ...next }, analyticsChosen);
}

/** Re-saves and re-syncs stored choices without inventing an analytics choice. */
export function retryPrivacySync(): void {
  analyticsConfirmed = false;
  diagnosticsConfirmed = false;
  diagnosticsError = null;
  revision += 1;
  store(read(), hasDeviceAnalyticsChoice());
}

function store(choices: PrivacyChoices, analyticsChosen: boolean): void {
  error = null;
  storageFailed = !save(choices, analyticsChosen);
  if (storageFailed) clearSignupAttribution();
  if (storageFailed) error = translate(libNoticesMessages, "privacySaveFailed");
  emit();
}

export function setPrivacyAccount(
  next: string | null | undefined,
  analyticsExcluded = false,
): void {
  if (account === next) {
    if (accountAnalyticsExcluded === analyticsExcluded) return;
    accountAnalyticsExcluded = analyticsExcluded;
    emit();
    return;
  }
  if (account !== undefined || typeof next === "string")
    clearSignupAttribution();
  account = next;
  accountAnalyticsExcluded = analyticsExcluded;
  analyticsConfirmed = false;
  accountAnalytics = false;
  error = null;
  emit();
}

export function confirmAnalyticsConsent(
  userId: string,
  enabled: boolean,
): void {
  if (account !== userId) return;
  if (
    !enabled &&
    read().analytics &&
    // An account-level withdrawal wins over a stale device choice.
    !save({ ...read(), analytics: false }, true)
  )
    storageFailed = true;
  accountAnalytics = enabled;
  analyticsConfirmed = enabled && read().analytics;
  error = null;
  emit();
}

export function confirmDiagnosticsConsent(enabled: boolean): void {
  diagnosticsConfirmed = enabled && read().diagnostics;
  diagnosticsError = null;
  emit();
}

export function privacyDiagnosticsSyncFailed(message: string): void {
  diagnosticsConfirmed = false;
  diagnosticsError = message;
  emit();
}

export function privacySyncFailed(message: string): void {
  analyticsConfirmed = false;
  accountAnalytics = false;
  error = message;
  emit();
}

snapshot = currentSnapshot();
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== KEY && event.key !== null) return;
    const next = read();
    if (!next.analytics) clearSignupAttribution();
    if (next.analytics !== snapshot.analytics) {
      analyticsConfirmed = false;
      revision += 1;
    }
    if (next.diagnostics !== snapshot.diagnostics) diagnosticsConfirmed = false;
    emit();
  });
}
